import { Button, CollapsiblePanel, Icon, Panel, PanelGroup, PanelHandle, useCollapsiblePanelController } from "@qw/ui";
import type { ReactNode } from "react";
import { useMemo } from "react";

interface AuthoringEditorPanesProps {
  autoSaveId: string;
  children: ReactNode;
  details: ReactNode | ((controls: AuthoringEditorPanesDetailsControls) => ReactNode);
  detailsLabel: string;
  detailsPanelClassName?: string;
  editorPanelClassName?: string;
  panelIds: {
    details: string;
    editor: string;
  };
}

interface AuthoringEditorPanesDetailsControls {
  collapseButton: ReactNode;
}

export const AuthoringEditorPanes = ({
  autoSaveId,
  children,
  details,
  detailsLabel,
  detailsPanelClassName = "",
  editorPanelClassName = "",
  panelIds,
}: AuthoringEditorPanesProps) => {
  const controller = useCollapsiblePanelController();

  const defaultLayout = useMemo(
    () => ({
      [panelIds.editor]: 72,
      [panelIds.details]: 28,
    }),
    [panelIds.details, panelIds.editor]
  );
  const panelIdList = useMemo(() => [panelIds.editor, panelIds.details], [panelIds.details, panelIds.editor]);

  const expandLabel = `Expand ${detailsLabel} pane`;
  const collapseLabel = `Collapse ${detailsLabel} pane`;
  const collapseButton = (
    <Button
      type="button"
      variant="ghost"
      size="2xs"
      display="icon"
      aria-label={collapseLabel}
      aria-expanded={!controller.isCollapsed}
      title={collapseLabel}
      onClick={controller.toggle}
      className="-my-1"
    >
      <Icon icon="keyboard_arrow_right" />
    </Button>
  );
  const detailsContent =
    typeof details === "function" ? (
      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden">{details({ collapseButton })}</div>
    ) : (
      <>
        <div className="flex h-10 flex-none items-center justify-end border-b px-3">{collapseButton}</div>
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden">{details}</div>
      </>
    );

  return (
    <PanelGroup
      direction="horizontal"
      autoSaveId={autoSaveId}
      defaultLayout={defaultLayout}
      panelIds={panelIdList}
      className="min-h-0 min-w-0 flex-auto"
    >
      <Panel
        id={panelIds.editor}
        minSize="50%"
        defaultSize="72%"
        className={`relative min-h-0 min-w-0 overflow-hidden ${editorPanelClassName}`}
      >
        {children}
        {controller.isCollapsed && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            display="icon"
            aria-label={expandLabel}
            aria-expanded={false}
            title={expandLabel}
            onClick={controller.toggle}
            className="absolute right-3 top-3 z-20 bg-background/95 shadow-sm"
          >
            <Icon icon="keyboard_arrow_left" />
          </Button>
        )}
      </Panel>
      <PanelHandle />
      <CollapsiblePanel
        controller={controller}
        id={panelIds.details}
        minSize="18%"
        defaultSize="28%"
        className={`min-h-0 min-w-0 overflow-hidden ${detailsPanelClassName}`}
        contentClassName="flex min-h-0 min-w-0 flex-col bg-background"
      >
        {detailsContent}
      </CollapsiblePanel>
    </PanelGroup>
  );
};
